import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import { differenceInMinutes } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { LoadingButton } from 'atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { ScarcityIcon } from 'atoms/icons/ScarcityIcon';
import { SorareLogo } from 'atoms/icons/SorareLogo';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import {
  BodyM,
  BodyS,
  BrandL,
  BrandXL,
  BrandXS,
  HeadlineL,
} from 'atoms/typography';
import { Dialog } from 'components/dialog';
import { DialogKey } from 'components/navigation/WithDialogs';
import { AnyRewardConfig } from 'components/rewards/AnyRewardConfig';
import { Avatar } from 'components/user/Avatar';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useMutation } from 'hooks/graphql/useMutation';
import { useCloseDialog } from 'hooks/navigation/useCloseDialog';
import { useDialogParam } from 'hooks/navigation/useDialogParam';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import { formatScarcity } from 'lib/cards';
import { glossary } from 'lib/glossary';
import { formatGqlErrors, isType } from 'lib/gql';
import { mergeRewardsConfig } from 'lib/rewards';

import type {
  CreateConnectedTasks,
  CreateConnectedTasksVariables,
} from './__generated__/index.graphql';
import bg from './assets/bg.webp';

const Header = styled(Vertical)`
  height: 40px;
  background: var(--background-image);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

const Targets = styled(Vertical)`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-black);
  border-radius: var(--double-unit);
`;

const SAHorizontal = styled(Horizontal)`
  justify-content: space-around;
  width: 100%;
`;

const StyledDialog = styled(Dialog)`
  background: url(${bg}) no-repeat center center / cover;
`;

const StyledScarcityIcon = styled(ScarcityIcon)`
  transform: rotate(20deg);
`;

const CREATE_CONNECTED_TASK_MUTATION = gql`
  mutation CreateConnectedTasks($input: createConnectedTasksInput!) {
    createConnectedTasks(input: $input) {
      tasks {
        id
        target
        name
        title
        rarity
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
          ...mergeRewardsConfig_anyRewardConfigInterface
        }
        ... on ThresholdsDuoConnectionTask {
          id
          connectedUser {
            slug
            ...Avatar_user
          }
        }
      }
      errors {
        message
      }
    }
  }
  ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ${Avatar.fragments.user}
  ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<CreateConnectedTasks, CreateConnectedTasksVariables>;

export const JoinConnectedTaskDialog = () => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { currentUser } = useCurrentUserContext();
  const [tasksPreview, setCreateConnectedTasksResultPreview] =
    useState<CreateConnectedTasks['createConnectedTasks']>();
  const closeDialog = useCloseDialog();

  const connectCampaign = useDialogParam(DialogKey.connect_campaign);
  const connectedUserSlug = useDialogParam(DialogKey.connected_user_slug);
  const hasJustSignup =
    currentUser && differenceInMinutes(Date.now(), currentUser?.createdAt) < 5;

  const [createConnectedTasks, { loading, called }] = useMutation(
    CREATE_CONNECTED_TASK_MUTATION,
    { showErrorsInForm: true }
  );

  const onClose = useCallback(() => {
    closeDialog(
      DialogKey.connected_user_slug,
      [DialogKey.connect_campaign],
      true
    );
  }, [closeDialog]);

  const mutate = useCallback(
    async (simulate: boolean) => {
      if (!connectCampaign || !connectedUserSlug) {
        return;
      }

      const { data } = await createConnectedTasks({
        variables: {
          input: {
            campaignSlug: connectCampaign,
            connectedUserSlug,
            simulate,
          },
        },
      });

      if (simulate) {
        setCreateConnectedTasksResultPreview(data?.createConnectedTasks);
      } else {
        if ((data?.createConnectedTasks?.errors.length || 0) > 0) {
          return;
        }
        onClose();
      }
    },
    [connectCampaign, connectedUserSlug, createConnectedTasks, onClose]
  );

  useEffect(() => {
    if (connectCampaign && connectedUserSlug && !called && !hasJustSignup) {
      mutate(true);
    }
  }, [called, connectCampaign, connectedUserSlug, hasJustSignup, mutate]);

  if (!connectedUserSlug || !connectCampaign || !called) {
    return null;
  }

  const connectedUser = tasksPreview?.tasks?.find(
    task => task?.__typename === 'ThresholdsDuoConnectionTask'
  )?.connectedUser;

  const sumCashRewards = mergeRewardsConfig(
    tasksPreview?.tasks?.flatMap(task => task?.rewardConfigs) || []
  ).filter(reward => isType(reward, 'MonetaryRewardConfig'))[0];

  if (hasJustSignup) {
    onClose();
    return null;
  }

  return (
    <StyledDialog
      open={!!currentUser && !!tasksPreview}
      onClose={onClose}
      hideHeader
      maxWidth="xs"
      footer={
        tasksPreview?.errors.length === 0 && (
          <Horizontal grow>
            <Button color="red" size="medium" fullWidth onClick={onClose}>
              <FormattedMessage {...glossary.reject} />
            </Button>
            <LoadingButton
              color="primary"
              size="medium"
              onClick={async () => {
                mutate(false);
              }}
              loading={loading}
              fullWidth
            >
              <FormattedMessage {...glossary.accept} />
            </LoadingButton>
          </Horizontal>
        )
      }
    >
      {({ CloseButton }) => {
        if (tasksPreview?.errors.length) {
          return (
            <Content>
              <Vertical>
                <HeadlineL>
                  <FormattedMessage
                    defaultMessage="Sorry"
                    id="JoinConnectedTaskDialog.sorry"
                  />
                </HeadlineL>
                <BodyM color="var(--c-nd-600)">
                  {formatGqlErrors(tasksPreview?.errors)}
                </BodyM>
                <Button color="primary" size="medium" onClick={onClose}>
                  <FormattedMessage {...glossary.gotIt} />
                </Button>
              </Vertical>
            </Content>
          );
        }

        return (
          <>
            <Header>
              <CloseButtonWrapper>
                <CloseButton className="close-button" onClose={onClose} />
              </CloseButtonWrapper>
            </Header>
            <Content gap={5} center className="text-center">
              <CloseButtonWrapper>
                <CloseButton className="close-button" onClose={onClose} />
              </CloseButtonWrapper>
              <Vertical center>
                <SorareLogo size="small" />
                <BrandXL>{tasksPreview?.tasks?.[0]?.title}</BrandXL>
                <BodyM bold>
                  <FormattedMessage
                    defaultMessage="Duo pairing"
                    id="JoinConnectedTaskDialog.duoPairing"
                  />
                </BodyM>
              </Vertical>
              <Vertical gap={3} center>
                <Horizontal gap={4}>
                  {!!currentUser && (
                    <Avatar size={8} user={currentUser} rounded />
                  )}
                  <FontAwesomeIcon icon={faLink} />
                  {!!connectedUser && (
                    <Avatar size={8} user={connectedUser} rounded />
                  )}
                </Horizontal>
                {!!sumCashRewards && (
                  <BodyM color="var(--c-nd-600)">
                    <FormattedMessage
                      defaultMessage="Team up and complete the Duo Mission and you can win up to {amount}."
                      id="JoinConnectedTaskDialog.teamUpV3"
                      values={{
                        amount: (
                          <FormattedNumber
                            value={
                              toMonetaryAmount(sumCashRewards.monetaryAmount)
                                .usd
                            }
                            style="currency"
                            currency="USD"
                          />
                        ),
                      }}
                    />
                  </BodyM>
                )}
              </Vertical>
              <Targets gap={2} center className="text-center">
                <BrandXS>
                  <FormattedMessage
                    defaultMessage="Score targets"
                    id="JoinConnectedTaskDialog.scoreTargets"
                  />
                </BrandXS>
                <SAHorizontal>
                  {(tasksPreview?.tasks || [])
                    .filter(Boolean)
                    .map(({ rewardConfigs, target, rarity }) => {
                      return (
                        <Vertical key={rewardConfigs?.[0]?.id} gap={2}>
                          <Vertical gap={0.5}>
                            <Horizontal gap={0.5}>
                              <BrandL>{target}</BrandL>
                              <StyledScarcityIcon
                                rarity={rarity || 'mix'}
                                size={2.5}
                              />
                            </Horizontal>
                            <BodyS>{formatScarcity(rarity || 'mix')}</BodyS>
                          </Vertical>
                          <AnyRewardConfig
                            anyRewardConfig={rewardConfigs?.[0]}
                          />
                        </Vertical>
                      );
                    })}
                </SAHorizontal>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    defaultMessage="Reach the target in the same Game Week as your duo partner"
                    id="JoinConnectedTaskDialog.reachTargetV2"
                  />
                </BodyS>
              </Targets>
            </Content>
          </>
        );
      }}
    </StyledDialog>
  );
};
